import { Component, DestroyRef, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { ClientAnalyticsControllerService, ClientBrandTagResponse, ClientAnalyticsMediaOutletOption, ClientAnalyticsAuthorOption } from 'api-clipping';
import { ClientControllerService } from 'api-client';
import { SharedService } from '../../../shared/services/shared.service';
import { environment } from '../../../../environments/environment';
import { brandColor } from '../../../shared/components/sette/chart-theme';
import { FilterBarComponent, FilterState, createDefaultFilterState, BrandOption } from './filter-bar/filter-bar.component';
import { filterStateFromQueryParams, filterStateToQueryParams } from './shared/url-filter.util';
import { isCompetitorBrandKind } from './shared/analytics-request.util';
import { OverviewComponent } from './overview/overview.component';
import { CoverageDashboardComponent } from './coverage-dashboard/coverage-dashboard.component';
import { CoverageAnalysisComponent, AnalysisMetric } from './coverage-analysis/coverage-analysis.component';
import { BrandsComponent } from './brands/brands.component';
import { TopicsComponent } from './topics/topics.component';
import { SentimentComponent } from './sentiment/sentiment.component';
import { MediaComponent } from './media/media.component';
import { ReportsComponent } from './reports/reports.component';
import { AnalyticsUtilitiesComponent, UtilitiesSection } from './analytics-utilities/analytics-utilities.component';

// Задача 8: only these are real screens with their own filtered content - a `tab` query param
// that doesn't match one of them (hand-edited link, stale bookmark) is ignored rather than
// setting activeTab to garbage.
const VALID_TABS = new Set(['overview', 'coverage_dashboard', 'coverage_analysis', 'brands', 'topics', 'sentiment', 'media', 'reports', 'saved_views', 'exports']);
const VALID_ANALYSIS_METRICS = new Set(['Mentions', 'Reach', 'Impact']);

interface AnalyticsNavItem {
	id: string;
	label: string;
	icon: string;
	hasSubmenu?: boolean;
}

// Skeleton + filter bar: left menu, empty title pages, and the top filter bar (date range,
// brand, Filters drawer, Export button) ported from the analytics-design React prototype
// (Sidebar.tsx, FilterBar.tsx, DateRangePicker.tsx). No charts yet - those come in a later step.
@Component({
	selector: 'app-analytics-panel',
	standalone: true,
	imports: [
		MatIconModule,
		TranslateModule,
		FilterBarComponent,
		OverviewComponent,
		CoverageDashboardComponent,
		CoverageAnalysisComponent,
		BrandsComponent,
		TopicsComponent,
		SentimentComponent,
		MediaComponent,
		ReportsComponent,
		AnalyticsUtilitiesComponent,
	],
	templateUrl: './analytics-panel.component.html',
	styleUrl: './analytics-panel.component.scss',
})
export class AnalyticsPanelComponent implements OnInit {
	activeTab = 'overview';
	filters: FilterState = createDefaultFilterState(
		this.translate.currentLang,
		this.translate.instant('ANALYTICS_DATE_PICKER.PRESET_THIS_QUARTER'),
		this.translate.instant('ANALYTICS_DATE_PICKER.VS'),
	);
	// SETTE redesign (CLIPPING-598): sidebar logo + "Client" block at the bottom of the nav,
	// same source as the main app navbar (SharedService.resellerSettingsContext.navBar.logo)
	// and the client profile page (ClientControllerService.getClientDetails().name).
	logoUrl?: string;
	clientName = '';
	// This client's real Company/Competitor tags (GET /v1/client/analytics/brands), replacing the
	// old hardcoded 6-brand sample list - loaded once here and passed down to both the filter bar
	// (brand quick-select) and Overview (focus-brand resolution for its KPIs).
	brandOptions: BrandOption[] = [];
	rawBrandTags: ClientBrandTagResponse[] = [];
	// This client's real media outlets/authors (GET /v1/client/analytics/filter-options),
	// replacing the Filters drawer's "coming soon" placeholders - loaded once here, same pattern
	// as brandOptions above.
	mediaOutletOptions: ClientAnalyticsMediaOutletOption[] = [];
	authorOptions: ClientAnalyticsAuthorOption[] = [];

	// The "Export PDF" button in the filter bar (a:1 from the PDF options proposal) - a one-click
	// browser-side snapshot of whatever screen is currently open, not a server render. That means
	// it looks exactly like what's on screen (same fonts/colors/zoom), costs nothing on the
	// backend, and needed no new service - but it is a literal screenshot: one tall single-page
	// PDF, not paginated, and anything not actually rendered (collapsed accordions, rows scrolled
	// out of a fixed-height table) won't be in it either. This is deliberately the simpler, more
	// visually-faithful tradeoff vs. the server-side option used for scheduled e-mail reports
	// (ReportGenerationServiceImpl), which renders its own lightweight Chart.js document instead.
	@ViewChild('screenContent') screenContentRef?: ElementRef<HTMLElement>;
	pdfExporting = false;

	// Задача 8: set the moment a `brands` query param is read (synchronously, in ngOnInit, before
	// loadBrandTags' HTTP response can arrive) - tells loadBrandTags whether the URL already
	// picked an explicit brand selection, so its own "select all brands" default doesn't clobber
	// a link that intentionally selected only some.
	private urlHasBrandsParam = false;

	// NG0203: takeUntilDestroyed() needs an injection context. ngOnInit (and anything it calls,
	// like loadSidebarIdentity below) isn't one - only the constructor/field-initializer is. So
	// the DestroyRef is captured here, at field-initialization time, and passed explicitly to
	// every takeUntilDestroyed() call in this component instead of relying on its implicit lookup.
	private readonly destroyRef = inject(DestroyRef);

	constructor(
		private readonly clientAnalyticsApi: ClientAnalyticsControllerService,
		private readonly apiClient: ClientControllerService,
		private readonly sharedService: SharedService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly translate: TranslateService
	) {}

	async exportScreenAsPdf(): Promise<void> {
		const el = this.screenContentRef?.nativeElement;
		if (!el || this.pdfExporting) return;

		this.pdfExporting = true;
		try {
			const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#f3f4f6', useCORS: true });
			const imgData = canvas.toDataURL('image/png');
			const pdf = new jsPDF({ unit: 'px', format: [canvas.width, canvas.height] });
			pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
			const datePart = new Date().toISOString().slice(0, 10);
			pdf.save(`${(this.activeTitle || 'analytics').replace(/\s+/g, '_')}-${datePart}.pdf`);
		} finally {
			this.pdfExporting = false;
		}
	}

	ngOnInit(): void {
		// Задача 8: ActivatedRoute.queryParamMap replays the current URL synchronously on
		// subscribe, then emits again on every later navigation - including the browser's
		// Back/Forward, which never goes through setActiveTab/onFiltersChange/syncUrl below. This
		// one subscription is what makes Back/Forward restore the previous filter: it only ever
		// reads from the URL into local state, it never calls router.navigate itself, so there is
		// no feedback loop with syncUrl's own writes.
		this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => this.applyQueryParams(params));
		this.loadBrandTags();
		this.loadFilterOptions();
		this.loadSidebarIdentity();
	}

	private applyQueryParams(params: ParamMap): void {
		const tab = params.get('tab');
		if (tab && VALID_TABS.has(tab)) {
			this.activeTab = tab;
		}
		const metric = params.get('metric');
		if (metric && VALID_ANALYSIS_METRICS.has(metric)) {
			this.analysisInitialMetric = metric as AnalysisMetric;
			this.currentAnalysisMetric = metric as AnalysisMetric;
		}
		this.urlHasBrandsParam = params.has('brands');
		this.filters = filterStateFromQueryParams(
			params,
			this.filters,
			this.translate.currentLang,
			this.translate.instant('ANALYTICS_DATE_PICKER.VS'),
			this.translate.instant('ANALYTICS_DATE_PICKER.NO_COMPARISON'),
			(key: string) => this.translate.instant(key),
		);
	}

	// Writes the currently active screen + filter into the URL's query params - called from every
	// place that changes either (setActiveTab, onFiltersChange, onOpenSavedView; onPanelNavigate
	// goes through setActiveTab). Deliberately NOT called from applyQueryParams/ngOnInit, so simply
	// opening a link (or pressing Back/Forward) never itself pushes a new history entry - only a
	// real user-driven change does.
	private syncUrl(): void {
		const metric = this.activeTab === 'coverage_analysis' ? this.currentAnalysisMetric : undefined;
		const queryParams = filterStateToQueryParams(this.filters, this.activeTab, metric);
		this.router.navigate([], { relativeTo: this.route, queryParams });
	}

	private loadSidebarIdentity(): void {
		this.sharedService.resellerSettingsContext.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response) => {
			const logo = response?.navBar?.logo;
			if (logo) {
				this.logoUrl = logo.startsWith('http') ? logo : `${environment.reseller.assets}/images/${logo}`;
			}
		});
		this.apiClient
			.getClientDetails()
			.pipe(take(1))
			.subscribe({
				next: (res) => (this.clientName = res?.name || ''),
				error: () => (this.clientName = ''),
			});
	}

	private loadFilterOptions(): void {
		this.clientAnalyticsApi
			.getFilterOptions()
			.pipe(take(1))
			.subscribe({
				next: (res) => {
					this.mediaOutletOptions = res.mediaOutlets ?? [];
					this.authorOptions = res.authors ?? [];
				},
				error: () => {
					this.mediaOutletOptions = [];
					this.authorOptions = [];
				},
			});
	}

	private loadBrandTags(): void {
		this.clientAnalyticsApi
			.getClientBrandTags()
			.pipe(take(1))
			.subscribe({
				next: (tags) => {
					this.rawBrandTags = tags ?? [];
					this.brandOptions = this.rawBrandTags.map(
						(t): BrandOption => ({
							id: t.tagId!,
							name: t.name!,
							role: isCompetitorBrandKind(t.brandKind) ? 'competition' : 'client',
							color: brandColor(t.tagId, this.rawBrandTags),
						})
					);
					// Задача 8: a URL that already named an explicit brand selection keeps it -
					// only fall back to "select every brand" when the link/reload didn't specify one.
					if (!this.urlHasBrandsParam) {
						this.filters = { ...this.filters, brands: this.brandOptions.map((b) => b.id) };
					}
				},
				error: () => {
					this.rawBrandTags = [];
					this.brandOptions = [];
				},
			});
	}

	// Задача (Фаза 6): getters, not readonly fields, so the sidebar/title relabel immediately on a
	// language switch - same reasoning as every other translate.instant()-backed getter in this
	// module (see OverviewComponent.detailPages).
	get coreItems(): AnalyticsNavItem[] {
		const t = (key: string) => this.translate.instant(key);
		return [
			{ id: 'overview', label: t('ANALYTICS_NAV.OVERVIEW'), icon: 'dashboard' },
			{ id: 'coverage', label: t('ANALYTICS_NAV.COVERAGE'), icon: 'layers', hasSubmenu: true },
			{ id: 'brands', label: t('ANALYTICS_NAV.BRANDS'), icon: 'sell' },
			{ id: 'topics', label: t('ANALYTICS_NAV.TOPICS'), icon: 'label' },
			{ id: 'sentiment', label: t('ANALYTICS_NAV.SENTIMENT'), icon: 'mood' },
			{ id: 'media', label: t('ANALYTICS_NAV.MEDIA'), icon: 'tv' },
			{ id: 'reports', label: t('ANALYTICS_NAV.REPORTS'), icon: 'bar_chart' },
		];
	}

	get utilityItems(): AnalyticsNavItem[] {
		const t = (key: string) => this.translate.instant(key);
		return [
			{ id: 'saved_views', label: t('ANALYTICS_NAV.SAVED_VIEWS'), icon: 'bookmark' },
			{ id: 'exports', label: t('ANALYTICS_NAV.EXPORTS'), icon: 'download' },
		];
	}

	private get pageTitles(): Record<string, string> {
		const t = (key: string) => this.translate.instant(key);
		return {
			overview: t('ANALYTICS_NAV.OVERVIEW'),
			coverage_dashboard: t('ANALYTICS_NAV.COVERAGE_DASHBOARD'),
			coverage_analysis: t('ANALYTICS_NAV.COVERAGE_ANALYSIS'),
			brands: t('ANALYTICS_NAV.BRANDS'),
			topics: t('ANALYTICS_NAV.TOPICS'),
			sentiment: t('ANALYTICS_NAV.SENTIMENT'),
			media: t('ANALYTICS_NAV.MEDIA'),
			reports: t('ANALYTICS_NAV.REPORTS'),
			saved_views: t('ANALYTICS_NAV.SAVED_VIEWS'),
			exports: t('ANALYTICS_NAV.EXPORTS'),
		};
	}

	get activeTitle(): string {
		return this.pageTitles[this.activeTab] ?? '';
	}

	get isCoverageGroupActive(): boolean {
		return this.activeTab === 'coverage_dashboard' || this.activeTab === 'coverage_analysis';
	}

	setActiveTab(id: string): void {
		this.activeTab = id;
		this.syncUrl();
	}

	// Overview's, Coverage Dashboard's and Coverage Analysis's cards/links emit a design-level
	// target id that doesn't always match this panel's own tab id scheme 1:1 - this maps the ones
	// that differ.
	// 'coverage' has no page of its own here (it's only a sidebar group) -> Coverage Dashboard.
	// 'mentions'/'reach'/'impact' all route to Coverage Analysis (mirroring the design's own
	// AnalysisDashboard, which the 'analysis'/'mentions'/'reach'/'impact' routes all share) -
	// analysisInitialMetric below carries which of the 3 tabs it should open on.
	// 'sources' is a dead link target in the source design (CoverageDashboard.tsx and
	// AnalysisDashboard.tsx both point buttons at it, but no screen in the whole prototype - not
	// Sidebar.tsx's nav, not DashboardContent.tsx's own routing - ever defines a 'sources' tab);
	// mapped to Media, the closest real destination for "view media channels / all media / all
	// authors".
	private readonly navigateAliases: Record<string, string> = {
		coverage: 'coverage_dashboard',
		mentions: 'coverage_analysis',
		reach: 'coverage_analysis',
		impact: 'coverage_analysis',
		sources: 'media',
	};

	private readonly analysisMetricAliases: Record<string, AnalysisMetric> = {
		mentions: 'Mentions',
		reach: 'Reach',
		impact: 'Impact',
	};

	analysisInitialMetric: AnalysisMetric = 'Mentions';
	// Live-tracked (unlike analysisInitialMetric, which is only the seed for navigating in) via
	// CoverageAnalysisComponent's metricChange output - what Save View actually captures.
	currentAnalysisMetric: AnalysisMetric = 'Mentions';

	onPanelNavigate(targetId: string): void {
		const metric = this.analysisMetricAliases[targetId];
		if (metric) {
			this.analysisInitialMetric = metric;
			this.currentAnalysisMetric = metric;
		}
		this.setActiveTab(this.navigateAliases[targetId] ?? targetId);
	}

	onFiltersChange(filters: FilterState): void {
		this.filters = filters;
		this.syncUrl();
	}

	// Coverage Analysis's own Mentions/Reach/Impact sub-tab - kept in the URL too (the `metric`
	// param) so a shared/bookmarked/Back-Forward'd link reopens on the same sub-tab, not just the
	// same screen.
	onAnalysisMetricChange(metric: AnalysisMetric): void {
		this.currentAnalysisMetric = metric;
		this.syncUrl();
	}

	// AnalyticsScreen enum values ('COVERAGE_DASHBOARD', ...) lowercase to exactly this panel's own
	// tab ids ('coverage_dashboard', ...), so no separate reverse-lookup table is needed.
	onOpenSavedView(event: { screen: string; filters: FilterState; coverageAnalysisTab?: string }): void {
		this.filters = event.filters;
		if (event.coverageAnalysisTab) {
			this.analysisInitialMetric = event.coverageAnalysisTab as AnalysisMetric;
			this.currentAnalysisMetric = event.coverageAnalysisTab as AnalysisMetric;
		}
		this.setActiveTab(event.screen.toLowerCase());
	}
}
